import { useEffect, useState, useDeferredValue } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { status } from 'constants/general';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import categoriesStrings from 'modules/categories/strings';
import { submitPriceList, editPriceList } from 'modules/categories/categoriesSlice';
import { onChangeAndValidationPrice } from './PharmaciesPriceListModal.helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendLoaderStyle,
  ItemContainer,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendBlueButtonStyle,
  InputField,
  FieldLabel,
  FieldContainer,
  WarningMsg,
  InputFieldWrapper,
  InputBasicField,
  InputFieldIcon,
} from './PharmaciesPriceListModal.style';
import { CategoryAttributeEnum } from '../../categories/pharmacies/enum';

const PriceListModal = ({ onClose, priceListRecordInfo, healthPlanId }) => {
  const dispatch = useDispatch();
  const isEdit = !!priceListRecordInfo?.id;
  const submitPriceListState = useSelector(state => state.categories.submitPriceList);
  const editPriceListState = useSelector(state => state.categories.editPriceList);
  const isSubmitting =
    status.SUBMITING === submitPriceListState.status ||
    status.SUBMITING === editPriceListState.status;
  const isFailed = status.FAIL === submitPriceList.status || status.FAIL === editPriceList.status;

  const [priceListInfo, setPriceListInfo] = useState({
    healthPlanId,
    priceListName: priceListRecordInfo?.name || '',
    priceListLocalDiscount:
      priceListRecordInfo?.priceListConfigurations?.find(
        config => config.categoryAttributeId === CategoryAttributeEnum.LocalDicount,
      )?.priceValue ?? '',
    priceListImportedDiscount:
      priceListRecordInfo?.priceListConfigurations?.find(
        config => config.categoryAttributeId === CategoryAttributeEnum.ImportedDicount,
      )?.priceValue ?? '',
    priceListKey: priceListRecordInfo?.priceListKey || '',
    priceListActivationStatus: priceListRecordInfo?.active,
    priceListId: priceListRecordInfo?.id,
  });

  const onClickClose = () => {
    onClose();
    setPriceListInfo({
      priceListName: '',
      priceListLocalDiscount: '',
      priceListImportedDiscount: '',
    });
  };

  const prevSubmitPriceListStateStatus = useDeferredValue(submitPriceListState.status);
  const prevEditPriceListStateStatus = useDeferredValue(editPriceListState.status);
  useEffect(() => {
    if (
      (status.SUCCESS === submitPriceListState.status &&
        prevSubmitPriceListStateStatus !== submitPriceListState.status) ||
      (status.SUCCESS === editPriceListState.status &&
        editPriceListState.status !== prevEditPriceListStateStatus)
    ) {
      onClickClose();
    }
  }, [submitPriceListState.status, editPriceListState.status]);

  const validatePriceListData = () => {
    let isValid = true;
    if (
      !priceListInfo.priceListName.trim() ||
      !(priceListInfo.priceListLocalDiscount >= 0) ||
      !(priceListInfo.priceListImportedDiscount >= 0)
    )
      isValid = false;
    return isValid;
  };

  const onSave = () => {
    if (isEdit) {
      // Update action
      const payload = {
        id: priceListInfo?.priceListId,
        data: {
          healthPlanId: Number(priceListInfo.healthPlanId),
          name: priceListInfo.priceListName,
          active: priceListInfo?.priceListActivationStatus,
        },
      };
      dispatch(editPriceList(payload));
    } else {
      // create action
      const payload = {
        healthPlanId: Number(priceListInfo.healthPlanId),
        name: priceListInfo.priceListName,
        priceListTypeId: 5,
        categoryId: 2,
        priceListConfigurations: [
          {
            priceValue: Number(priceListInfo.priceListLocalDiscount),
            categoryAttributeId: CategoryAttributeEnum.LocalDicount,
          },
          {
            priceValue: Number(priceListInfo.priceListImportedDiscount),
            categoryAttributeId: CategoryAttributeEnum.ImportedDicount,
          },
        ],
      };
      dispatch(submitPriceList(payload));
    }
  };

  return (
    <Popup showCloseBtn={false}>
      <PopupWrapper>
        <PopupTitleContainer>
          <PopupTitleWrapper>
            <PopupTitle>
              {isEdit
                ? categoriesStrings.priceLists.editPriceList
                : categoriesStrings.priceLists.createAPriceList}
            </PopupTitle>
            <PopupTitleSpan
              onClick={() => {
                onClickClose();
              }}
            />
          </PopupTitleWrapper>
        </PopupTitleContainer>

        <Container>
          <div
            style={{
              width: '680px',
            }}
          >
            <div>
              <ItemContainer>
                <FieldContainer>
                  <FieldLabel>Price list name</FieldLabel>

                  <InputField
                    id="pricelist-name"
                    placeholder="Price lists name"
                    value={priceListInfo.priceListName}
                    onChange={e => {
                      setPriceListInfo({
                        ...priceListInfo,
                        priceListName: e.target.value,
                      });
                    }}
                  />
                </FieldContainer>
                <FieldContainer>
                  <FieldLabel>Local discount</FieldLabel>
                  <InputFieldWrapper disabled={isEdit}>
                    <InputBasicField
                      disabled={isEdit}
                      id="price-local-discount"
                      value={priceListInfo.priceListLocalDiscount}
                      placeholder="0"
                      onChange={e => {
                        onChangeAndValidationPrice(e.target.value, () =>
                          setPriceListInfo({
                            ...priceListInfo,
                            priceListLocalDiscount: e.target.value,
                          }),
                        );
                      }}
                    />
                    <InputFieldIcon>%</InputFieldIcon>
                  </InputFieldWrapper>
                </FieldContainer>
                <FieldContainer>
                  <FieldLabel>Imported discount</FieldLabel>
                  <InputFieldWrapper disabled={isEdit}>
                    <InputBasicField
                      disabled={isEdit}
                      id="price-imported-discount"
                      value={priceListInfo.priceListImportedDiscount}
                      placeholder="0"
                      onChange={e => {
                        onChangeAndValidationPrice(e.target.value, () =>
                          setPriceListInfo({
                            ...priceListInfo,
                            priceListImportedDiscount: e.target.value,
                          }),
                        );
                      }}
                    />
                    <InputFieldIcon>%</InputFieldIcon>
                  </InputFieldWrapper>
                </FieldContainer>
                {isFailed && <WarningMsg>Something went wrong !</WarningMsg>}
              </ItemContainer>
            </div>
            <ButtonsContainer>
              <Button
                onClick={() => {
                  onClickClose();
                }}
                btnText="Cancel"
                extendButtonStyle={extendGrayButtonStyle}
                key="dismiss-popup"
                disabled={isSubmitting === status.SUBMITING}
              />
              <Button
                onClick={() => {
                  onSave();
                }}
                btnText={
                  isEdit
                    ? categoriesStrings.priceLists.editPriceList
                    : categoriesStrings.priceLists.createAPriceList
                }
                extendButtonStyle={extendBlueButtonStyle}
                key="save-btn"
                disabled={!validatePriceListData()}
                extendLoaderStyle={extendLoaderStyle}
                isLoading={isSubmitting}
              />
            </ButtonsContainer>
          </div>
        </Container>
      </PopupWrapper>
    </Popup>
  );
};

PriceListModal.propTypes = {
  onClose: PropTypes.func,
  priceListKey: PropTypes.string,
  priceListRecordInfo: PropTypes.shape({
    priceListName: PropTypes.string,
    priceListPrice: PropTypes.number,
    priceListKey: PropTypes.string,
  }),
};

export default PriceListModal;
